import {
  contactLink,
  contactRows as legacyRows,
  normalizeDigits,
} from "@1-now/booking-core";
import { formatPhone, phoneHref } from "utils/formatPhone";

export type ContactRowType = "phone" | "whatsapp" | "email" | "text";

export interface ContactRow {
  id?: string;
  type: ContactRowType;
  /** Optional operator label ("Office"). Empty for legacy rows. */
  label: string;
  /** The value as shown. */
  display: string;
  /** tel:, https://wa.me/, mailto:, or "" when the row is not a link. */
  href: string;
  /** Icon name, same as type. */
  icon: ContactRowType;
}

const TYPES = ["phone", "whatsapp", "email", "text"];

// A legacy row whose link opens WhatsApp (wa.me/..., api.whatsapp.com/send?phone=...).
const WHATSAPP_HREF = /^https?:\/\/(?:wa\.me|api\.whatsapp\.com)(?=[/?#]|$)/i;

/**
 * Footer / Contact page rows from `contact_items` keypoints.
 * New rows: `link` is the type, `description` the raw value, `heading` the label.
 * Legacy rows (`link` empty): `description` is "tel:..." / "mailto:..." and `heading`
 * is the shown text. When a legacy heading holds a different number than its tel:
 * link, the heading is what the renter sees, so it is what gets dialled.
 */
export const buildContactRows = (list: unknown): ContactRow[] => {
  const out: ContactRow[] = [];
  (Array.isArray(list) ? list : []).forEach((raw: any) => {
    const heading = typeof raw?.heading === "string" ? raw.heading.trim() : "";
    const value = typeof raw?.description === "string" ? raw.description.trim() : "";
    const type = String(raw?.link ?? "").trim().toLowerCase();

    if (TYPES.includes(type)) {
      if (!value) return;
      const t = type as ContactRowType;
      const digits = normalizeDigits(value);
      let href = "";
      if (t === "phone" && digits) href = phoneHref(value);
      else if (t === "whatsapp" && digits) href = `https://wa.me/${digits}`;
      else if (t === "email") href = `mailto:${value}`;
      const display = t === "phone" || t === "whatsapp" ? formatPhone(value) : value;
      out.push({ id: raw?.id, type: t, label: heading, display, href, icon: t });
      return;
    }

    // Legacy row: read it the package's way, then fix a mismatched tel: link.
    const row = legacyRows([raw])[0];
    if (!row || (!row.heading && !row.description)) return;
    let href: string = row.href || "";
    let display: string = row.heading || row.description;
    if (/^tel:/i.test(value)) {
      // The number the renter sees is the one dialled, read like a new phone row: "tel:923095182731"
      // shows "+92 309 5182731" and dials its "+" (1NOW-11109 reopen).
      const shown = normalizeDigits(heading).length >= 7 ? heading : heading ? "" : value;
      href = phoneHref(shown || value) || href;
      if (shown && /^(tel:)?[+\d\s().-]+$/i.test(shown)) display = formatPhone(shown);
    }
    const icon: ContactRowType = href.startsWith("tel:")
      ? "phone"
      : href.startsWith("mailto:")
      ? "email"
      : WHATSAPP_HREF.test(href)
      ? "whatsapp"
      : "text";
    out.push({
      id: raw?.id,
      type: icon,
      label: "",
      display,
      href,
      icon,
    });
  });
  return out;
};

/**
 * The company's own phone and email as rows (1NOW-11109), for a footer or Contact page whose
 * operator has set no contact rows. `companyDetails` is the website details the Contact page
 * already reads. A stored "tel:" or "mailto:" is a scheme, not part of what the number says.
 */
export const companyContactRows = (companyDetails: any): ContactRow[] => {
  const out: ContactRow[] = [];
  const phone = String(companyDetails?.company_phone ?? "").trim().replace(/^tel:/i, "");
  if (phone) {
    const href = normalizeDigits(phone) ? phoneHref(phone) : "";
    out.push({ id: "company-phone", type: "phone", label: "", display: formatPhone(phone), href, icon: "phone" });
  }
  const mail = String(companyDetails?.company_email ?? "").trim();
  if (mail) {
    const link = contactLink(mail);
    out.push({ id: "company-email", type: "email", label: "", display: link.label, href: link.href ?? "", icon: "email" });
  }
  return out;
};
